<template>
  <div class="eb-root">
    <!-- rail-always: the erm family shows a rail in classic too (the Event
         Log's .od-rail), so the legacy banner here read as a deviation -->
    <FiPageRail title="Event Board" :chip="contextTenantName" rail-always>
      <template #actions>
        <!-- same form as the Event Log's, from the same component -->
        <b-button size="sm" variant="primary" class="eb-act-right" title="Add a new event"
                  aria-label="Add entry" @click="$refs.addEntryModal.open()">
          <i class="fa fa-plus"></i><span class="eb-btn-label">&nbsp;Add entry</span>
        </b-button>
        <erm-board-help />
      </template>
    </FiPageRail>

    <!-- a new event lands in the New lane, so the board reloads on create.
         the "event added" toast opens it through open-event (from=board) -->
    <od-add-entry-modal
      ref="addEntryModal"
      modal-id="eb-add-entry-modal"
      :tenant="tenant"
      :sos-enabled="sosEnabled"
      @created="onEntryCreated"
      @open-event="openEventById"
    />

    <div v-if="loading && !events.length && !error" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="reload()">Retry</b-button>
    </div>

    <div v-else :class="{ 'eb-stale': loading, 'eb-filters-collapsed': !filtersExpanded }">
      <!-- phone-only filters toggle, mirroring the Event Log: everything but
           the search pill folds away so a phone opens straight to the lanes.
           the badge counts narrowed dimensions, search excluded — it has its
           own always-visible field. display:none above 600px. -->
      <button
        type="button"
        class="eb-filter-toggle eb-mobile-only"
        :aria-expanded="filtersExpanded ? 'true' : 'false'"
        @click="filtersExpanded = !filtersExpanded"
      >
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="eb-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>

      <div class="eb-scope-row">
        <button type="button" :class="['eb-chip', { 'eb-chip--active': assigneeFilter === MINE }]"
                :aria-pressed="assigneeFilter === MINE ? 'true' : 'false'"
                @click="assigneeFilter = MINE">Mine ({{ myOpenCount }})</button>
        <button type="button" :class="['eb-chip', { 'eb-chip--active': assigneeFilter === '' }]"
                :aria-pressed="assigneeFilter === '' ? 'true' : 'false'"
                @click="assigneeFilter = ''">Everyone</button>
        <button type="button" :class="['eb-chip', { 'eb-chip--active': assigneeFilter === UNASSIGNED }]"
                :aria-pressed="assigneeFilter === UNASSIGNED ? 'true' : 'false'"
                @click="assigneeFilter = UNASSIGNED">Unassigned ({{ unassignedOpenCount }})</button>
        <b-form-select v-model="assigneeDropdown" :options="assigneeOptions" size="sm"
                       class="eb-scope-select" aria-label="Filter by assignee" />
        <!-- narrows the lanes as you type, over the events already loaded.
             the drag hint that used to sit here is gone: the lane notes now
             say what a drop will do while the card is actually in flight. -->
        <div class="eb-search ml-auto">
          <i class="fa fa-search eb-search-icon" aria-hidden="true"></i>
          <input v-model="search" type="text" class="eb-search-input"
                 placeholder="Search events…" aria-label="Search events">
          <button v-if="search" type="button" class="eb-search-clear"
                  aria-label="Clear search" title="Clear search" @click="search = ''">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- second row: what the cards are, rather than whose they are. same
           Type and Severity vocabulary as the Event Log's chips, so the two
           surfaces filter by the same words. no Status group — the lanes ARE
           the status. -->
      <div class="eb-filter-row">
        <span class="eb-group" role="group" aria-label="Type">
          <span class="eb-group-label">Type</span>
          <button v-for="opt in TYPE_FILTERS" :key="'t-' + opt.value" type="button"
                  :class="['eb-chip', { 'eb-chip--active': typeFilters.includes(opt.value) }]"
                  :aria-pressed="typeFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('typeFilters', opt.value)">{{ opt.text }}</button>
        </span>
        <span class="eb-group" role="group" aria-label="Severity">
          <span class="eb-group-label">Severity</span>
          <button v-for="opt in SEVERITY_FILTERS" :key="'sv-' + opt.value" type="button"
                  :class="['eb-chip', { 'eb-chip--active': severityFilters.includes(opt.value) }]"
                  :aria-pressed="severityFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('severityFilters', opt.value)">{{ opt.text }}</button>
        </span>
        <!-- driver / vehicle options come from the rows that survive every
             OTHER filter, so the lists only offer values that will return
             something — and picking one does not collapse its own list. -->
        <b-form-select v-model="driverFilter" :options="driverOptions" size="sm"
                       class="eb-filter-select" aria-label="Filter by driver" />
        <b-form-select v-model="vehicleFilter" :options="vehicleOptions" size="sm"
                       class="eb-filter-select" aria-label="Filter by vehicle" />
        <!-- the Resolved window chips live in the Resolved lane's header now
             (FI-2113): the control sits on the one lane it governs, so it can
             no longer read as a board-wide date range (the #1722 misread). -->
      </div>

      <!-- the lanes are capped, so "nothing here" is not the same as "nothing
           exists" — offer the way back and, when a search is what emptied the
           board, the surface that searches the whole history. -->
      <div v-if="isFiltered && !matchCount" class="eb-no-matches">
        No loaded events match these filters.
        <button type="button" class="eb-link-btn" @click="clearFilters">Clear filters</button>
        <template v-if="search.trim()">
          or
          <router-link :to="{ name: 'ops_desk', query: { tenant: tenant, search: search.trim() } }">
            search the full history in the Event Log
          </router-link>
        </template>
      </div>

      <div class="eb-board" ref="board">
        <div v-for="col in columnDefs" :key="col.status"
             class="eb-col" :class="['eb-col--' + col.status, dropStateClass(col.status)]">
          <div class="eb-col-head">
            <span class="eb-status-chip" :class="'eb-status-chip--' + col.status">
              <i class="fa" :class="statusIcon(col.status)"></i>
              {{ statusLabel(col.status) }}
            </span>
            <span class="eb-col-count">{{ columns[col.status].length }}</span>
          </div>
          <!-- while a card is in flight the note stops describing the lane and
               starts answering "what happens if I let go here?" the Resolved
               note leads with the window chips (FI-2113): the control IS the
               "last N days" part of the description, so the words are gone. -->
          <div class="eb-col-note" :class="noteStateClass(col.status)">
            <span>{{ laneNote(col) }}</span>
            <!-- chips ride the right edge (the dashboard-widget header
                 arrangement), clear of the description text -->
            <span v-if="col.status === RESOLVED" class="eb-lane-window" role="group" aria-label="Resolved window">
              <button v-for="d in RESOLVED_WINDOWS" :key="'rw-' + d" type="button"
                      :class="['eb-lane-chip', { 'eb-lane-chip--active': resolvedWindow === d }]"
                      :aria-pressed="resolvedWindow === d ? 'true' : 'false'"
                      @click="resolvedWindow = d">{{ d }}d</button>
            </span>
          </div>
          <div class="eb-col-scroll"
               :class="laneShadowClasses('eb-col-scroll', col.status)"
               :ref="'laneScroll-' + col.status"
               @scroll.passive="onLaneScroll(col.status, $event)">
          <draggable :list="columns[col.status]" :sort="false"
                     :group="{ name: 'eb-board', pull: canPullFrom(col.status), put: true }"
                     :move="validateMove" :disabled="saving || isMobileView"
                     ghost-class="eb-card--ghost" drag-class="eb-card--dragging"
                     :animation="180"
                     :force-fallback="true" :fallback-on-body="true" :fallback-tolerance="4"
                     class="eb-col-body" @change="onColumnChange(col.status, $event)"
                     @start="draggingFrom = col.status" @end="draggingFrom = null">
            <div v-for="e in columns[col.status]" :key="e.id"
                 class="eb-card" :data-event-id="e.id"
                 :class="{
                   'eb-card--resolved': col.status === RESOLVED,
                   'eb-card--high': e.severity === 'high',
                   'eb-card--stale': isStale(e, col.status),
                   'eb-card--locked': !canPullFrom(col.status),
                   'eb-card--landed': flashes[e.id] === 'landed',
                   'eb-card--rejected': flashes[e.id] === 'rejected',
                   'eb-card--arrived': flashes[e.id] === 'arrived',
                 }"
                 @click="openEvent(e)">
              <div class="eb-card-top">
                <span class="eb-type-chip" :class="'eb-type-chip--' + e.entry_type">
                  <i class="fa" :class="entryTypeIcon(e.entry_type)"></i>
                  {{ entryTypeLabel(e.entry_type) }}
                </span>
                <span v-if="showSeverityChip(e.severity)" class="eb-sev-chip"
                      :class="['eb-sev-chip--' + severityKey(e.severity), { 'eb-sev-chip--dim': col.status === RESOLVED }]"
                      :title="'Severity: ' + severityLabel(e.severity)">
                  {{ severityLabel(e.severity) }}
                </span>
                <span class="eb-card-id ml-auto">#{{ e.id }}</span>
              </div>

              <!-- the full description, like the Event Log's: the card grows
                   to fit rather than clamping, so the title tooltip that used
                   to be the only way to read past line three is gone. -->
              <div class="eb-card-what">
                {{ e.what_happened || '(no description)' }}
              </div>

              <div v-if="e.category || e.driver_id || e.vehicle_id || e.charter_ref" class="eb-card-meta">
                <span v-if="e.category" class="eb-cat-chip">{{ categoryLabel(e.category) }}</span>
                <span v-if="e.driver_id" class="eb-meta"><i class="fa fa-user"></i>&nbsp;{{ e.driver_id }}</span>
                <span v-if="e.vehicle_id" class="eb-meta"><i class="fa fa-bus"></i>&nbsp;{{ e.vehicle_id }}</span>
                <span v-if="e.charter_ref" class="eb-meta eb-charter" :title="'Charter ' + e.charter_ref"><i class="fa fa-ticket"></i>&nbsp;{{ e.charter_ref }}</span>
              </div>

              <div class="eb-card-foot">
                <span v-if="isStale(e, col.status)" class="eb-flag eb-flag--stale">{{ ageInDays(e) }}d open</span>
                <span v-else-if="col.status === RESOLVED" class="eb-flag eb-flag--resolved">
                  {{ formatDate(e.updated_at || e.created_at) }}
                </span>
                <span v-else class="eb-flag">{{ formatDate(e.created_at) }}</span>
                <span class="ml-auto">
                  <span v-if="isMine(e)" class="eb-you-chip">You</span>
                  <span v-else-if="e.assigned_user_id" class="eb-assignee">{{ displayUserName(e.assigned_user_id) }}</span>
                  <span v-else class="eb-assignee eb-assignee--none">Unassigned</span>
                </span>
              </div>
            </div>
          </draggable>

          <!-- FI-2113 a lane with nothing to show says so, instead of
               rendering a bare drop slot. suppressed while the server holds
               more rows (the load-more zone owns that story), while a card
               is in flight (the ghost needs the space), and worded as "no
               matches" when it is the filters that emptied the lane. -->
          <div v-if="!columns[col.status].length && !loading && !truncated[col.status] && !draggingFrom"
               class="eb-col-empty">
            {{ laneEmptyText(col) }}
          </div>

          <!-- FI-2113 per-lane infinite scroll: this zone is the sentinel —
               scrolling near it loads the lane's next page, announced only
               by the brief loading line, and an exhausted lane just ends.
               the one spoken state is the auto-load cap: the board stops
               fetching while the server still holds more, and stopping
               silently there would misrepresent the lane as complete. -->
          <div v-if="truncated[col.status]" class="eb-col-more" :ref="'laneMore-' + col.status">
            <template v-if="loadingMore[col.status]">
              <i class="fa fa-refresh fa-spin" aria-hidden="true"></i> loading more…
            </template>
            <template v-else-if="laneLoadedCount(col.status) >= LANE_AUTO_LOAD_CAP">
              newest {{ LANE_AUTO_LOAD_CAP }} loaded ·
              <router-link :to="logLinkFor(col.status)">see all in the Event Log</router-link>
            </template>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import draggable from 'vuedraggable'
import { list_ops_desk_events, transition_ops_desk_event_status } from '../networking/apis'
import { tenantContextDiffers, tenantDisplayName } from '../utils/tenant_context'
import swimLaneViewport from '../utils/swim_lane_viewport'
import ermLiveRefresh from '../mixins/erm_live_refresh'
import ErmBoardHelp from './erm_board_help.vue'
import OdAddEntryModal from './od_add_entry_modal.vue'
import {
  OPEN_STATUSES, STATUS_RESOLVED,
  statusLabel, statusIcon, canTransition,
  typeLabel as opsTypeLabel, entryTypeIcon,
  severityLabel, severityKey, showSeverityChip,
  categoryLabel as opsCategoryLabel, isCustomCategory, CUSTOM_CATEGORY_PREFIX,
} from '../utils/ops_labels'

// FI-2113 per-lane paging. each lane is one status-filtered query off the
// status gsi, fetched a page at a time: a small first page for a fast first
// paint, then the lane's own scroll sentinel pulls the next page. the
// auto-load cap stops a hard filter (which keeps the sentinel permanently
// in view) from silently draining a three-year history — past it the lane
// simply ends, and the Event Log stays the history surface.
const LANE_PAGE_SIZE = 50
const OPEN_FIRST_PAGE = 200
const LANE_AUTO_LOAD_CAP = 400

// the Resolved lane is a recent-closures view rather than a history, so it
// carries a window. the OPEN lanes deliberately do not: an event nobody has
// touched in four months is the most important card on this board, and a
// 7-day filter would hide exactly the work the stale flag exists to surface.
const RESOLVED_WINDOWS = [7, 30, 90]
const DEFAULT_RESOLVED_WINDOW = 7

// the fetch reaches back twice the widest window because the api can only
// bound on created_at — every gsi the list endpoint uses is keyed by it, and
// there is no index on updated_at. the lane is narrowed to "closed within N
// days" client-side, so the fetch has to cover events FILED long before they
// were closed. an event that took more than 180 days to resolve falls off the
// board; the lane's Event Log link is the way to those.
const RESOLVED_FETCH_DAYS = 180

// same vocabulary as the Event Log's chips, but as independent toggles
// rather than a one-of-N row: no "All" chip, an empty set means no
// narrowing, and picking two widens (Issue + Kudos shows both).
// 'incident' reads as 'Issue' — the stored value is frozen, the label is not.
const TYPE_FILTERS = [
  { value: 'incident', text: 'Issue' },
  { value: 'kudos', text: 'Kudos' },
  { value: 'note', text: 'Note' },
]
const SEVERITY_FILTERS = [
  { value: 'high', text: 'High' },
  { value: 'normal', text: 'Normal' },
  { value: 'low', text: 'Low' },
]

// an open event this old has stopped moving. mirrors nothing on the server —
// it is a board-only read of neglect, the way the coaching board reads
// overdue — so the threshold lives here.
const STALE_AFTER_DAYS = 7

// drag is off at or below this width. matches the @media (max-width: 768px)
// block at the foot of this file — the two must agree, or the card would
// still say "grab" while refusing to be dragged.
const MOBILE_MAX_WIDTH = 768

// how long a card keeps its landed / rejected / arrived pulse
const FLASH_MS = 1500
// a quiet pass that changed more cards than this (back from lunch) shows no
// pulses at all: the lanes just update
const MAX_ARRIVAL_PULSES = 12

// what a lane promises while a card is hovering over it. the workflow's own
// action labels read badly in the imperative ("Drop here to awaiting
// follow-up"), so the board words the promise itself.
const DROP_NOTES = {
  new: 'Drop here to mark new',
  in_progress: 'Drop here to start work',
  awaiting_followup: 'Drop here to await follow-up',
  resolved: 'Drop here to resolve',
}

export default {
  name: 'erm_board',
  components: { draggable, ErmBoardHelp, OdAddEntryModal },
  mixins: [swimLaneViewport, ermLiveRefresh],
  data() {
    return {
      loading: false,
      error: null,
      saving: false,
      events: [],
      // FI-1944 (#1816) slug -> the fleet's name for its own and renamed
      // categories, off the lane pages (category_labels)
      categoryLabels: {},

      // the swim lanes. draggable mutates these arrays on drop, so they are
      // plain data rebuilt from `events` — never computed.
      columns: { new: [], in_progress: [], awaiting_followup: [], resolved: [] },
      // per-lane: does the server hold more than what is loaded?
      truncated: { new: false, in_progress: false, awaiting_followup: false, resolved: false },
      // FI-2113 per-lane paging state: the resume cursor from the last
      // page, and whether a page is in flight
      nextTokens: { new: null, in_progress: null, awaiting_followup: null, resolved: null },
      loadingMore: { new: false, in_progress: false, awaiting_followup: false, resolved: false },

      // free-text narrowing over the loaded events, composed with the
      // assignee scope. client-side on purpose: the board already holds every
      // card it can show, so the lanes narrow as you type instead of paying
      // four round trips per keystroke.
      search: '',

      // ≤600px folds the filter controls behind a toggle so a phone opens on
      // the lanes rather than on three rows of chips (Event Log precedent).
      filtersExpanded: false,

      // ≤768px turns dragging OFF entirely. force-fallback starts a drag as
      // soon as a touch moves, which on a phone is indistinguishable from
      // scrolling the lane — cards got picked up every time you tried to
      // scroll past them. Rather than tune a hold delay, touch widths drop
      // the drag surface and change status the way the Event Log does: tap
      // the card, use Move to on the event. Kept in sync by a resize
      // listener wired in mounted().
      isMobileView: false,

      // empty = every type / every severity; otherwise the selected values
      typeFilters: [],
      severityFilters: [],
      // '' = every driver / vehicle; otherwise the exact stored id
      driverFilter: '',
      vehicleFilter: '',
      TYPE_FILTERS,
      SEVERITY_FILTERS,

      // '' = everyone, MINE = the signed-in user, UNASSIGNED = no assignee,
      // any other string = that user's username
      assigneeFilter: '',
      MINE: '__mine__',
      UNASSIGNED: '__unassigned__',
      RESOLVED: STATUS_RESOLVED,
      // how far back the Resolved lane reaches, in days. switching it is a
      // client-side narrowing of what is already loaded, not a refetch.
      resolvedWindow: DEFAULT_RESOLVED_WINDOW,
      RESOLVED_WINDOWS,

      // template-visible copy, for the at-cap note
      LANE_AUTO_LOAD_CAP,
      scopeInitialized: false,

      // the lane a card was picked up from, for the duration of the drag.
      // drives the lane highlighting: null means nothing is in flight.
      draggingFrom: null,
      // event id -> 'landed' | 'rejected'. a card that has just come to rest
      // pulses, so the eye can follow it to where it ended up.
      flashes: {},
      flashTimers: [],
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    tenant() {
      return this.$route.query.tenant || (this.user && this.user.tenant)
    },
    // admin deep-link context: name the tenant being rendered whenever it
    // differs from the navbar selection, matching the Event Log's rail.
    contextTenantName() {
      return tenantContextDiffers(this.$route, this.user) ? tenantDisplayName(this.user, this.tenant) : ''
    },
    // reopening a resolved event is admin-only server-side. this mirrors
    // event_manager._is_admin exactly — admin OR customer-admin — and now
    // agrees with ops_desk.vue's canEditAdminFields, which used to run one
    // role wider and offered a reopen button that 401'd.
    // FI-1820: the add-entry form offers an inline Bus SOS link on
    // breakdown / accident, but only where the tenant runs SOS.
    sosEnabled() {
      const f = this.user && this.user.features && this.user.features[this.tenant]
      return !!(f && f.bus_sos_enabled)
    },
    canReopen() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin'
    },
    columnDefs() {
      // the reopen hint names a gesture, so it only appears where the gesture
      // exists — on touch widths the drag surface is off entirely. the lane's
      // window chips render right before this note, so the note never repeats
      // the "last N days" they already say.
      const canDrag = this.canReopen && !this.isMobileView
      return [
        { status: 'new', note: 'filed, nobody has picked it up', empty: 'No new events' },
        { status: 'in_progress', note: 'somebody is working it', empty: 'Nothing in progress' },
        { status: 'awaiting_followup', note: 'waiting on someone else', empty: 'Nothing waiting on follow-up' },
        { status: 'resolved', note: canDrag ? 'drag back to reopen' : 'recent closures',
          empty: `No closures in the last ${this.resolvedWindow} days` },
      ]
    },
    myOpenCount() {
      return this.events.filter(e => this.isMine(e) && OPEN_STATUSES.includes(e.status)).length
    },
    unassignedOpenCount() {
      return this.events.filter(e => !e.assigned_user_id && OPEN_STATUSES.includes(e.status)).length
    },
    scopedEvents() {
      return this.applyFilters(this.events)
    },
    matchCount() {
      return this.scopedEvents.length
    },
    driverOptions() {
      return this.distinctOptions('driver_id', 'driver', this.driverFilter, 'All drivers')
    },
    vehicleOptions() {
      return this.distinctOptions('vehicle_id', 'vehicle', this.vehicleFilter, 'All vehicles')
    },
    // the narrowing controls proper. the assignee scope and the Resolved
    // window are deliberately excluded: both always hold a value, so counting
    // them would mean the board is permanently "filtered".
    isFiltered() {
      return !!(this.search.trim() || this.typeFilters.length || this.severityFilters.length
        || this.driverFilter || this.vehicleFilter)
    },
    // badge on the mobile toggle: how many dimensions are narrowed while the
    // panel is folded away. everything counts, search included — it folds
    // away with the rest now, so nothing else on screen would show that a
    // search is trimming the lanes. Unlike isFiltered, the point here is to
    // say what is hidden behind the toggle, not whether the board is
    // filtered in the "nothing matches" sense.
    activeFilterCount() {
      let n = 0
      if (this.search.trim()) n++
      if (this.typeFilters.length) n++
      if (this.severityFilters.length) n++
      if (this.driverFilter) n++
      if (this.vehicleFilter) n++
      if (this.assigneeFilter) n++
      // the Resolved window is NOT counted: it lives on the lane header now,
      // visible even while the filter panel is folded away
      return n
    },
    assigneeOptions() {
      const by = {}
      this.events.forEach(e => {
        if (!e.assigned_user_id) return
        const row = by[e.assigned_user_id] = by[e.assigned_user_id] || { open: 0 }
        if (OPEN_STATUSES.includes(e.status)) row.open += 1
      })
      const people = Object.entries(by)
        .map(([username, row]) => ({
          value: username,
          text: `${this.displayUserName(username)}${row.open ? ` (${row.open} open)` : ''}`,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
      return [{ value: null, text: 'By assignee…' }, ...people]
    },
    assigneeDropdown: {
      get() {
        return ['', this.MINE, this.UNASSIGNED].includes(this.assigneeFilter)
          ? null : this.assigneeFilter
      },
      set(value) {
        this.assigneeFilter = value || ''
      },
    },
  },
  watch: {
    // the navbar tenant switcher mutates user.tenant in place without
    // remounting the page (ops_desk.vue precedent). event ids are a dense
    // per-tenant counter, so they collide across tenants — drop every trace
    // of the old tenant's board before a drag can act on it.
    tenant() {
      this.events = []
      this.categoryLabels = {}
      this.columns = { new: [], in_progress: [], awaiting_followup: [], resolved: [] }
      this.truncated = { new: false, in_progress: false, awaiting_followup: false, resolved: false }
      this.nextTokens = { new: null, in_progress: null, awaiting_followup: null, resolved: null }
      this.loadingMore = { new: false, in_progress: false, awaiting_followup: false, resolved: false }
      this.assigneeFilter = ''
      this.search = ''
      this.typeFilters = []
      this.severityFilters = []
      this.driverFilter = ''
      this.vehicleFilter = ''
      this.resolvedWindow = DEFAULT_RESOLVED_WINDOW
      this.scopeInitialized = false
      this.loadEvents()
    },
    assigneeFilter() {
      this.rebuildColumns()
    },
    search() {
      this.rebuildColumns()
    },
    typeFilters() {
      this.rebuildColumns()
    },
    severityFilters() {
      this.rebuildColumns()
    },
    driverFilter() {
      this.rebuildColumns()
    },
    vehicleFilter() {
      this.rebuildColumns()
    },
    // no refetch: the fetch already reaches past the widest window, so
    // switching is a client-side narrowing of what is loaded.
    resolvedWindow() {
      this.rebuildColumns()
    },
  },
  created() {
    this.loadEvents()
  },
  mounted() {
    this._onResize = () => { this.isMobileView = window.innerWidth <= MOBILE_MAX_WIDTH }
    this._onResize()
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    this.flashTimers.forEach(clearTimeout)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    this.teardownLaneObservers()
  },

  methods: {
    statusLabel, statusIcon, entryTypeIcon, severityLabel, severityKey, showSeverityChip,
    entryTypeLabel(v) { return v ? opsTypeLabel(v) : '-' },
    // FI-1944 (#1816) a category's name for this fleet
    categoryLabel(v) {
      return opsCategoryLabel(v, this.categoryLabels) || '-'
    },
    // a lane page's names join the map (every lane carries its own)
    mergeCategoryLabels(labels) {
      if (!labels || typeof labels !== 'object') return
      this.categoryLabels = { ...(this.categoryLabels || {}), ...labels }
    },
    // same display rule as the Event Log: drop the domain on @ addresses.
    displayUserName(username) {
      if (!username) return ''
      const at = username.indexOf('@')
      return at > 0 ? username.slice(0, at) : username
    },
    formatDate(iso) {
      if (!iso) return '—'
      const m = moment(iso)
      // events get revisited months later, so only this year's may drop it
      return m.year() === moment().year() ? m.format('MMM D') : m.format('MMM D, YYYY')
    },
    ageInDays(e) {
      if (!e.created_at) return 0
      return Math.max(0, moment().diff(moment(e.created_at), 'days'))
    },
    isStale(e, status) {
      return OPEN_STATUSES.includes(status) && this.ageInDays(e) >= STALE_AFTER_DAYS
    },
    isMine(e) {
      const me = this.user && this.user.username
      return !!(me && e.assigned_user_id === me)
    },
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || fallback
    },

    logLinkFor(status) {
      return { name: 'ops_desk', query: { tenant: this.tenant, status } }
    },

    // deliberately the same fields the Event Log's server-side search covers
    // (event_manager._matches_search), so the same words find the same events
    // on both surfaces. the id matches EXACTLY, never by substring: ids are a
    // dense per-tenant counter, so a bare "12" would otherwise drag in #112,
    // #120-#129, #212... FI-2197 (#1813) the charter follows the same rule:
    // an all-digit needle must equal a whole number in it. every check below
    // returns only on a hit, so no block can shadow the ones after it.
    matchesSearch(e, needle) {
      if (e.id !== undefined && e.id !== null && String(e.id).toLowerCase() === needle) return true
      // the linked roster spellings (driver_ref_name, vehicle_ref) ride along
      // because the Today board joins on them ("063" vs "63"). FI-2198 (#1820)
      // the customer the internal form records rides along too
      const fields = ['driver_id', 'vehicle_id', 'driver_ref_name', 'vehicle_ref', 'customer',
                      'what_happened', 'action_items', 'follow_up_resolution', 'coaching_note']
      if (fields.some(f => e[f] != null && String(e[f]).toLowerCase().includes(needle))) return true
      // charters are typed by hand ("15691", "15691-0 american cruise") and
      // booking numbers are dense like ids, so a bare number matches a whole
      // number, never a piece of one; any other needle is a substring
      const charter = e.charter_ref ? String(e.charter_ref).toLowerCase() : ''
      if (charter) {
        const hit = /^[0-9]+$/.test(needle)
          ? (charter.match(/[0-9]+/g) || []).includes(needle)
          : charter.includes(needle)
        if (hit) return true
      }
      // the location is stored as {text}: match the text only, never the
      // object, or "text" would match every located row
      const location = e.location
      if (location && typeof location === 'object' && location.text &&
          String(location.text).toLowerCase().includes(needle)) return true
      // the category both as stored ("hotel_issue") and as a human types it,
      // and (FI-1944) by the fleet's name for it, so a renamed or custom
      // category is found by the name on the card. a custom key is matched
      // without its custom_ prefix, which no card shows, or "tom" would find
      // every one of the fleet's own categories (as the server does)
      let category = e.category ? String(e.category).toLowerCase() : ''
      if (isCustomCategory(category)) category = category.slice(CUSTOM_CATEGORY_PREFIX.length)
      if (category && (category.includes(needle) || category.replace(/_/g, ' ').includes(needle))) return true
      if (e.category && opsCategoryLabel(e.category, this.categoryLabels).toLowerCase().includes(needle)) return true
      // legacy sheet rows keep their original wording in extra_fields, so a
      // "hotel" search would otherwise miss every imported hotel event
      const extras = e.extra_fields
      if (extras && typeof extras === 'object' && Object.keys(extras).some(k =>
        extras[k] != null && String(extras[k]).toLowerCase().includes(needle))) return true
      return false
    },

    // one filter pipeline for every narrowing control. `skip` names a
    // dimension to leave out, which is how the driver and vehicle dropdowns
    // ask "what would be left if my own filter were not applied?" — without
    // it, choosing a driver would immediately collapse that dropdown to the
    // single value just chosen.
    applyFilters(rows, skip = '') {
      if (skip !== 'assignee') {
        if (this.assigneeFilter === this.UNASSIGNED) {
          rows = rows.filter(e => !e.assigned_user_id)
        } else if (this.assigneeFilter) {
          const target = this.assigneeFilter === this.MINE
            ? (this.user && this.user.username) : this.assigneeFilter
          rows = rows.filter(e => e.assigned_user_id === target)
        }
      }
      // the Resolved lane is bounded by when the event was CLOSED, not when it
      // was filed — a month-old complaint settled yesterday is a recent
      // closure. updated_at is the close stamp (update_event_status writes
      // it); rows that never transitioned through the ui fall back to filing.
      const closedAfter = moment().subtract(this.resolvedWindow, 'days')
      rows = rows.filter(e => e.status !== STATUS_RESOLVED
        || moment(e.updated_at || e.created_at).isAfter(closedAfter))
      // an empty chip set is not a filter — it means every value is welcome
      if (skip !== 'type' && this.typeFilters.length) {
        rows = rows.filter(e => this.typeFilters.includes(e.entry_type))
      }
      if (skip !== 'severity' && this.severityFilters.length) {
        // severity postdates the first events, and an absent one renders as
        // Normal on the card — so it has to answer to the Normal chip too
        // (the api's own normal filter does this with a not_exists check).
        rows = rows.filter(e => this.severityFilters.includes(e.severity || 'normal'))
      }
      if (skip !== 'driver' && this.driverFilter) {
        rows = rows.filter(e => e.driver_id === this.driverFilter)
      }
      if (skip !== 'vehicle' && this.vehicleFilter) {
        rows = rows.filter(e => e.vehicle_id === this.vehicleFilter)
      }
      const needle = this.search.trim().toLowerCase()
      if (skip !== 'search' && needle) rows = rows.filter(e => this.matchesSearch(e, needle))
      return rows
    },

    // distinct values for one field over the rows that survive every OTHER
    // filter, so the list only ever offers choices that return something. the
    // current selection is kept even when the rest of the board filters it
    // away, or the select would blank out while still filtering.
    distinctOptions(field, dimension, selected, placeholder) {
      const seen = new Set(
        this.applyFilters(this.events, dimension)
          .map(e => (e[field] || '').trim())
          .filter(Boolean),
      )
      if (selected) seen.add(selected)
      const values = [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      return [{ value: '', text: placeholder }, ...values.map(v => ({ value: v, text: v }))]
    },
    // the chip rows are independent toggles, not a one-of-N choice: an empty
    // set means "every value", so there is no "All" chip to press — you
    // deselect back to everything. replaces the array rather than mutating so
    // the watcher fires on the reference change.
    toggleFilter(name, value) {
      const list = this[name]
      this[name] = list.includes(value)
        ? list.filter(v => v !== value)
        : [...list, value]
    },

    // clears the narrowing controls only — the assignee scope stays where the
    // viewer put it, and the Resolved window is a lane setting, not a filter.
    clearFilters() {
      this.search = ''
      this.typeFilters = []
      this.severityFilters = []
      this.driverFilter = ''
      this.vehicleFilter = ''
    },

    // the query params for one lane's page. one status-filtered query per
    // lane keeps every lane off the tenant date gsi (which would page
    // newest-first across all statuses and leave the older open work
    // invisible below the cap). resolved only: bound the fetch so a
    // three-year archive is never drained in one go — `from` filters
    // created_at (the gsi sort key), which is why it reaches back further
    // than any window offered.
    laneQuery(status, limit) {
      return {
        status,
        limit,
        from: status === STATUS_RESOLVED
          ? moment().subtract(RESOLVED_FETCH_DAYS, 'days').format('YYYY-MM-DD')
          : undefined,
      }
    },
    // first page of every lane, in parallel. every request skips the
    // full-history aggregate drain — the board renders no chip counts.
    async loadEvents() {
      this.loading = true
      this.error = null
      // a full load is a user action: it ends a 403 halt of the quiet
      // passes and forgets their failure backoff
      this.liveRefreshReset()
      const requestTenant = this.tenant
      // every full load starts a new pagination generation: a lane page
      // still in flight from before this load belongs to a dataset that no
      // longer exists, and loadMore drops it on this counter
      this._loadGen = (this._loadGen || 0) + 1
      const gen = this._loadGen
      try {
        const lanes = this.columnDefs.map(c => c.status)
        // open lanes' first page reaches to the old 200-cap depth: the
        // Mine-default scope and the assignee counts are seeded from this
        // page, and a 50-row seed missed exactly the stale old assignments
        // this board exists to surface. later pages stream in 50s.
        const responses = await Promise.all(lanes.map(status => list_ops_desk_events(
          this.tenant, {
            ...this.laneQuery(status, status === STATUS_RESOLVED ? LANE_PAGE_SIZE : OPEN_FIRST_PAGE),
            skip_counts: 1,
          },
        )))
        if (this.tenant !== requestTenant || this._loadGen !== gen) return
        const events = []
        const truncated = {}
        const nextTokens = {}
        const labels = {}
        responses.forEach((resp, i) => {
          const { value: { data = {} } = {} } = resp || {}
          events.push(...(data.items || []))
          Object.assign(labels, data.category_labels || {})
          nextTokens[lanes[i]] = data.next_token || null
          truncated[lanes[i]] = !!data.next_token
        })
        this.events = events
        // a full load starts the name map over
        this.categoryLabels = labels
        this.truncated = truncated
        this.nextTokens = nextTokens
        if (!this.scopeInitialized) {
          this.assigneeFilter = this.myOpenCount > 0 ? this.MINE : ''
          this.scopeInitialized = true
        }
        this.rebuildColumns()
        this.$nextTick(() => this.setupLaneObservers())
      } catch (e) {
        this.error = this.apiErrorMessage(e, 'Failed to load the event board.')
      } finally {
        this.loading = false
      }
    },
    reload() {
      return this.loadEvents()
    },
    // FI-2215 an entry filed from Add entry: reload the lanes, then pulse its
    // card with the arrival ring so the eye finds it (the dialog's toast
    // names it). only the new id; a card outside the scope just never shows
    async onEntryCreated(data) {
      const tenant = this.tenant
      await this.reload()
      const id = data && data.event_id
      // a fleet switch mid-reload: ids repeat across fleets, so the pulse
      // would land on another fleet's card
      if (!id || this.tenant !== tenant) return
      this.flashCard(id, 'arrived')
      // the New lane sorts oldest first within a severity, so the card can
      // land below the lane's fold. 'nearest' leaves a card in view alone
      this.$nextTick(() => {
        const card = this.$el && this.$el.querySelector('.eb-card[data-event-id="' + id + '"]')
        if (!card || typeof card.scrollIntoView !== 'function') return
        const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        card.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' })
      })
    },
    // FI-2173 quiet pass. mixins/erm_live_refresh.js decides when; this
    // guard says whether the lanes can take it. the set mirrors what
    // loadEvents and loadMore refuse to run under; a state added to either
    // must be mirrored here.
    canSoftRefresh() {
      if (this.loading || this.saving || this.draggingFrom) return false
      if (Object.keys(this.loadingMore).some(k => this.loadingMore[k])) return false
      if (typeof document === 'undefined' || document.hidden) return false
      // bootstrap-vue's modal-manager (2.0.0-rc.28, helpers/modal-manager.js)
      // puts modal-open on body while any b-modal is shown, so this covers
      // the add-entry modal, dirty or not. bv-internal: pinned
      // by tests/unit/erm_board_refresh.spec.js
      if (document.body && document.body.classList.contains('modal-open')) return false
      return true
    },
    // re-fetch to the depth the lane already holds plus one page of headroom
    // for arrivals, never past the cap
    softLaneLimit(status) {
      return Math.min(LANE_AUTO_LOAD_CAP, this.laneLoadedCount(status) + LANE_PAGE_SIZE)
    },
    // the same four lane queries as loadEvents, folded into what is on
    // screen. never touches loading / error, so no dim, no spinner, no error
    // card; a failure rejects to the mixin, which backs off.
    async softRefresh() {
      const requestTenant = this.tenant
      const gen = this._loadGen
      const dropGen = this._dropGen
      const lanes = this.columnDefs.map(c => c.status)
      const responses = await Promise.all(lanes.map(status => this.fetchLanePage(status)))
      // a full reload or tenant switch mid-flight owns the dataset now
      if (this.tenant !== requestTenant || this._loadGen !== gen) return
      // loadMore's rule: never rebuild the lane arrays under a drag, nor
      // while a drop's patch is out (the rebuild would snap the card back);
      // a page fetched before a drop is stale for that card, so the drop
      // counter throws it away and the next pass re-syncs
      while (this.draggingFrom || this.saving) {
        await new Promise(resolve => setTimeout(resolve, 150))
      }
      if (this.tenant !== requestTenant || this._loadGen !== gen || this._dropGen !== dropGen) return
      const pages = lanes.map((status, i) => {
        const { value: { data = {} } = {} } = responses[i] || {}
        this.mergeCategoryLabels(data.category_labels)
        return { status, items: data.items || [], nextToken: data.next_token || null }
      })
      const changedIds = this.mergeEvents(pages)
      this.rebuildColumns()
      this.$nextTick(() => this.setupLaneObservers())
      if (changedIds.length && changedIds.length <= MAX_ARRIVAL_PULSES) {
        // a landed / rejected pulse already on the card wins
        changedIds.forEach(id => { if (!this.flashes[id]) this.flashCard(id, 'arrived') })
      }
    },
    // network seam for softRefresh, stood in for by the spec
    fetchLanePage(status) {
      return list_ops_desk_events(
        this.tenant,
        { ...this.laneQuery(status, this.softLaneLimit(status)), skip_counts: 1 },
        { noAuthRedirect: true },
      )
    },
    // fold fresh lane pages into `events` without replacing it: existing
    // card objects are updated in place (keys, flashes and lane scroll
    // survive), unknown ids are appended, and the vanish rule is bounded
    // per lane. the api pages newest-first while the open lanes sort
    // oldest-first, so "absent from the fresh page" only means gone inside
    // the page's created_at window; with no next_token the page is
    // exhaustive and every absent card in that lane has left it. unknown
    // ids older than the lane's loaded window are left to loadMore (they
    // sit past the lane's own cursor), so a lane never creeps toward the
    // cap on its own. returns the ids that are new or whose status /
    // updated_at changed.
    mergeEvents(pages) {
      const byId = new Map(this.events.map(e => [e.id, e]))
      const freshEverywhere = new Set()
      pages.forEach(p => p.items.forEach(e => freshEverywhere.add(e.id)))
      const at = e => String(e.created_at || '')
      const vanished = new Set()
      const changed = []
      const appended = []
      pages.forEach(({ status, items, nextToken }) => {
        const exhaustive = !nextToken
        const oldestFresh = items.length ? at(items[items.length - 1]) : ''
        const lane = this.events.filter(e => e.status === status)
        const oldestLoaded = lane.reduce((m, e) => (m === null || at(e) < m ? at(e) : m), null)
        // an empty page that still carries a cursor says nothing about the
        // lane (the api drained its page budget), so it vanishes nothing
        const covers = exhaustive || items.length > 0
        lane.forEach(e => {
          if (!covers || freshEverywhere.has(e.id)) return
          if (exhaustive || at(e) >= oldestFresh) vanished.add(e.id)
        })
        let absorbed = true
        items.forEach(fresh => {
          const existing = byId.get(fresh.id)
          if (existing) {
            if (existing.status !== fresh.status || existing.updated_at !== fresh.updated_at) {
              changed.push(fresh.id)
            }
            Object.keys(fresh).forEach(k => this.$set(existing, k, fresh[k]))
            return
          }
          if (exhaustive || oldestLoaded === null || at(fresh) >= oldestLoaded) {
            byId.set(fresh.id, fresh)
            appended.push(fresh)
            changed.push(fresh.id)
          } else {
            absorbed = false
          }
        })
        // the fresh cursor resumes past the fresh page, which is only right
        // when everything on it is now loaded
        if (absorbed) {
          this.$set(this.nextTokens, status, nextToken)
          this.$set(this.truncated, status, !!nextToken)
        }
      })
      this.events = this.events.filter(e => !vanished.has(e.id)).concat(appended)
      return changed
    },

    // FI-2113 one more page for one lane, appended in place. the cursor is
    // the server's resume token, so pages never skip or repeat rows within
    // one generation; rows that ALSO arrive again because a drag moved them
    // into this lane's partition are dropped by the id dedupe.
    async loadMore(status) {
      const token = this.nextTokens[status]
      if (!token || this.loadingMore[status] || this.loading || this.draggingFrom) return
      this.$set(this.loadingMore, status, true)
      const requestTenant = this.tenant
      const gen = this._loadGen
      try {
        const resp = await list_ops_desk_events(this.tenant, {
          ...this.laneQuery(status, LANE_PAGE_SIZE), skip_counts: 1, next_token: token,
        })
        // a reload or tenant switch mid-flight replaced the dataset this
        // page belongs to — drop it, the fresh generation has its own cursor
        if (this.tenant !== requestTenant || this._loadGen !== gen) return
        // never rebuild the lane arrays under an active drag: sortablejs
        // holds indices captured at pickup, and re-sorting beneath it makes
        // the drop splice the wrong card. wait the drag out (they last
        // seconds), then re-check that no reload landed meanwhile.
        while (this.draggingFrom) {
          await new Promise(resolve => setTimeout(resolve, 150))
        }
        if (this.tenant !== requestTenant || this._loadGen !== gen) return
        const { value: { data = {} } = {} } = resp || {}
        this.mergeCategoryLabels(data.category_labels)
        const seen = new Set(this.events.map(e => e.id))
        const fresh = (data.items || []).filter(e => !seen.has(e.id))
        this.events = this.events.concat(fresh)
        this.$set(this.nextTokens, status, data.next_token || null)
        this.$set(this.truncated, status, !!data.next_token)
        this.rebuildColumns()
        this.$nextTick(() => this.setupLaneObservers())
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not load more events.'),
          { title: 'Event Response Manager', variant: 'danger', autoHideDelay: 4000 },
        )
      } finally {
        this.$set(this.loadingMore, status, false)
      }
    },
    laneLoadedCount(status) {
      return this.events.filter(e => e.status === status).length
    },
    // the definite claim ("No new events") is only honest when the data is
    // actually empty; when the filters or the assignee scope emptied the
    // lane, say that instead
    laneEmptyText(col) {
      if (this.isFiltered || this.assigneeFilter) return 'Nothing here matches the filters'
      return col.empty
    },
    // each lane's "load more" zone is watched inside that lane's own scroll
    // container: scrolling near it pulls the next page. rebuilt after every
    // load because the zone unmounts when a lane runs out of pages.
    setupLaneObservers() {
      this.teardownLaneObservers()
      this._laneObservers = []
      this.columnDefs.forEach(col => {
        let zone = this.$refs['laneMore-' + col.status]
        if (Array.isArray(zone)) zone = zone[0]
        let root = this.$refs['laneScroll-' + col.status]
        if (Array.isArray(root)) root = root[0]
        if (!zone || !root) return
        const io = new IntersectionObserver(entries => {
          if (!entries.some(en => en.isIntersecting)) return
          // never auto-load mid-drag: sortable's autoscroll can bring the
          // sentinel into view, and a rebuild under the drag corrupts it
          if (this.draggingFrom) return
          // past the cap, a permanently-visible sentinel (a hard filter
          // keeps lanes short) stops auto-draining history — the lane ends
          if (this.laneLoadedCount(col.status) >= LANE_AUTO_LOAD_CAP) return
          this.loadMore(col.status)
        }, { root, rootMargin: '120px 0px' })
        io.observe(zone)
        this._laneObservers.push(io)
      })
    },
    teardownLaneObservers() {
      (this._laneObservers || []).forEach(io => io.disconnect())
      this._laneObservers = []
    },

    rebuildColumns() {
      const cols = { new: [], in_progress: [], awaiting_followup: [], resolved: [] }
      this.scopedEvents.forEach(e => {
        if (cols[e.status]) cols[e.status].push(e)
      })
      // open lanes: the loud and the neglected first (high severity, then
      // oldest — an event nobody has touched in a month outranks this
      // morning's). resolved: most recently closed first.
      const sevRank = e => (e.severity === 'high' ? 0 : e.severity === 'low' ? 2 : 1)
      const openSort = (a, b) => sevRank(a) - sevRank(b)
        || String(a.created_at || '').localeCompare(String(b.created_at || ''))
      cols.new.sort(openSort)
      cols.in_progress.sort(openSort)
      cols.awaiting_followup.sort(openSort)
      cols.resolved.sort((a, b) => String(b.updated_at || b.created_at || '')
        .localeCompare(String(a.updated_at || a.created_at || '')))
      this.columns = cols
    },

    // resolved cards only leave the lane for an admin (the reopen path);
    // everyone else's resolved cards are read-only on the board.
    canPullFrom(status) {
      return status !== STATUS_RESOLVED || this.canReopen
    },

    // what this lane is to the card currently in flight: where it came from,
    // somewhere it may land, or somewhere the workflow will not let it. drives
    // the lane highlighting AND the note text, so the answer is computed once.
    dropState(status) {
      if (!this.draggingFrom) return ''
      if (status === this.draggingFrom) return 'source'
      return canTransition(this.draggingFrom, status, this.canReopen) ? 'target' : 'blocked'
    },
    dropStateClass(status) {
      const state = this.dropState(status)
      return state ? 'eb-col--drop-' + state : ''
    },
    noteStateClass(status) {
      const state = this.dropState(status)
      return (state === 'target' || state === 'blocked') ? 'eb-col-note--' + state : ''
    },
    laneNote(col) {
      const state = this.dropState(col.status)
      if (state === 'target') {
        return (this.draggingFrom === STATUS_RESOLVED && col.status === 'in_progress')
          ? 'Drop here to reopen' : DROP_NOTES[col.status]
      }
      if (state === 'blocked') return 'Not a move the workflow allows'
      return col.note
    },
    // a drop is over in a blink and the card is one of many — pulse it where
    // it came to rest so the outcome is visible without hunting for the id.
    flashCard(id, kind) {
      this.$set(this.flashes, id, kind)
      const timer = setTimeout(() => {
        this.$delete(this.flashes, id)
        this.flashTimers = this.flashTimers.filter(t => t !== timer)
      }, FLASH_MS)
      this.flashTimers.push(timer)
    },

    // sortablejs move hook: refuse anything the backend's transition table
    // would reject, so an illegal drop never round-trips into an error toast.
    validateMove(evt) {
      const event = evt.draggedContext.element
      const toEl = evt.to.closest('.eb-col')
      if (!toEl) return false
      const target = this.columnDefs.find(c => toEl.classList.contains('eb-col--' + c.status))
      if (!target) return false
      return canTransition(event.status, target.status, this.canReopen)
    },

    async onColumnChange(toStatus, evt) {
      if (!evt.added) return
      // a drop starts a new local generation: a quiet pass fetched before it
      // still holds this card under its old status, and softRefresh drops
      // that page on this counter (a refused drop already forces a fresh pass)
      this._dropGen = (this._dropGen || 0) + 1
      const event = evt.added.element
      this.saving = true
      try {
        const { value: { data = {} } = {} } = await transition_ops_desk_event_status(
          this.tenant, event.id, toStatus,
        ) || {}
        // apply the server's row in place rather than refetching all four
        // lanes; it carries the new status plus the updated_at the resolved
        // lane sorts on.
        const i = this.events.findIndex(e => e.id === event.id)
        if (i !== -1) this.$set(this.events, i, { ...this.events[i], ...data, status: toStatus })
        this.flashCard(event.id, 'landed')
        this.$bvToast.toast(
          `Event #${event.id} moved to ${statusLabel(toStatus)}.`,
          { title: 'Event Response Manager', variant: 'info', autoHideDelay: 3000 },
        )
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, `Could not move event #${event.id} to ${statusLabel(toStatus)}.`),
          { title: 'Event Response Manager', variant: 'danger', autoHideDelay: 6000 },
        )
        // the drag moved the card between the lane arrays but never touched
        // the event's own status, so the rebuild below snaps it back — the
        // pulse says which card just bounced.
        this.flashCard(event.id, 'rejected')
        // a refused move usually means someone else moved the card first:
        // once the bounce has been seen, a quiet pass lands it in its real lane
        this.liveRefreshArm('force', FLASH_MS)
      } finally {
        this.saving = false
        this.rebuildColumns()
      }
    },

    // from=board is what sends the detail's back arrow here rather than to the
    // Event Log (ops_desk.vue backTarget). push, not replace: the browser's
    // own back button should walk back to the board too.
    openEvent(event) {
      this.openEventById(event.id)
    },
    openEventById(id) {
      this.$router.push({
        name: 'ops_desk',
        query: { tenant: this.tenant, event_id: id, from: 'board' },
      }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.eb-root {
  max-width: none;
  /* FI-2113: the board fills the viewport and bleeds to the rail's edge,
     so the page bottom keeps only a sliver */
  padding: 12px 20px 2px 20px;
  text-align: left;
  font-size: 14px;
}
/* the rail bleeds into the page gutters so its TITLE TEXT lines up with the
   lanes below it rather than sitting 19px further in. -19px is exactly the
   rail's own inset (3px accent border + 16px padding), so pulling the box out
   by that much lands the text back on the root's 20px gutter; 1px of the
   root's 12px top / 20px side padding survives on each bled side. This is the
   Event Log's .od-rail--flush treatment, applied here rather than in
   FiPageRail so no other page shifts — FiPageRail's own comment describes
   this bleed but no page implements it, so every rail page has the same 19px
   step. Mobile keeps the stock chrome, matching the Event Log. */
@media (min-width: 601px) {
  .eb-root ::v-deep .fi-pr-rail { margin: -11px -19px 14px -19px; }
}
.eb-stale { opacity: 0.6; }
.eb-scope-row { display: flex; align-items: center; gap: 6px; margin: 14px 0 12px; flex-wrap: wrap; }
.eb-mobile-only { display: none; }
.eb-filter-toggle {
  align-items: center; gap: 7px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 7px 14px; margin: 0 0 10px;
  font-size: 13.5px; font-weight: 600;
  color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.eb-filter-toggle:hover { background: var(--fi-surface-sunken, #eef4fb); }
.eb-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.eb-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}
.eb-scope-select { max-width: 220px; font-size: 0.85em; }
/* driver / vehicle sit inline with the chip groups, so they stay narrow —
   ids and surnames are short, and the row reads as one band of controls
   rather than two wide fields. the value ellipsises rather than widening. */
.eb-filter-select {
  max-width: 132px; font-size: 0.85em;
  text-overflow: ellipsis;
}

/* search pill. paints no light background of its own, same reason the cards
   don't — the tokens carry it into midnight. */
.eb-search {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  min-width: 200px;
}
.eb-search:focus-within { border-color: var(--fi-ops-accent, #3a6e99); }
.eb-search-icon { font-size: 11px; color: var(--fi-text-muted, #8a94a1); }
.eb-search-input {
  flex: 1 1 auto; min-width: 0;
  background: transparent;
  border: 0; outline: none;
  font-size: 12.5px;
  color: var(--fi-text, #2c2c2a);
}
.eb-search-input::placeholder { color: var(--fi-text-faint, #9aa4b1); }
.eb-search-clear {
  background: none; border: 0; padding: 0;
  font-size: 11px; line-height: 1;
  color: var(--fi-text-muted, #8a94a1);
  cursor: pointer;
}
.eb-search-clear:hover { color: var(--fi-ops-accent, #3a6e99); }
.eb-no-matches {
  font-size: 12.5px;
  color: var(--fi-text-muted, #6b7280);
  margin: 0 0 10px;
}
.eb-link-btn {
  background: none; border: 0; padding: 0;
  font-size: inherit;
  color: var(--fi-ops-accent, #3a6e99);
  text-decoration: underline;
  cursor: pointer;
}
/* the filter row: what the cards are, under the row about whose they are */
.eb-filter-row {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0 0 12px;
}
.eb-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 3px 9px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
/* the active chip keeps its own fill on hover; the sunken wash under white
   text made it unreadable */
.eb-chip:hover:not(.eb-chip--active) { background: var(--fi-surface-sunken, #eef4fb); }
/* the window pills read as one control with the lane they narrow */
.eb-group { display: inline-flex; align-items: center; gap: 4px; }
/* the Resolved window governs one lane rather than the whole board, so it
   sits away from the filters that narrow every lane */
.eb-group--end { margin-left: auto; }
.eb-group-label {
  font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: var(--fi-text-muted, #6b7280);
}
.eb-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}

/* the board: four equal lanes, horizontal scroll when squeezed */
/* FI-2113 the board is a viewport-bound surface: it fills the space under
   the chrome above it (measured into --sl-board-top by the shared mixin)
   and each lane scrolls itself, so lane headers and every drop target stay
   on screen for the whole drag and the page itself never scrolls. */
.eb-board {
  display: flex; gap: 12px; align-items: stretch; overflow-x: auto;
  /* the 6px gap constant is mirrored in swim_lane_viewport.js's clamp
     detection -- change them together */
  /* vh, NOT dvh: the build's cssnano parses dvh fine on its own (see
     fleet_assistant_launcher) but NOT inside calc() -- a dvh calc fails
     the whole css minification step and breaks the pipeline. mobile
     browser chrome is covered by the mixin's measured shave instead. */
  height: calc(100vh - var(--sl-board-top, 320px) - 6px - var(--sl-board-shave, 0px));
  min-height: 320px;
  padding-bottom: 2px;
}
/* under horizon everything below the rail bleeds to the rail box's own
   edge (the rail pulls itself -19px out of the root's 20px gutter; the
   filter rows and lanes follow), so the whole page shares one left/right
   line -- matching the coaching board, whose root never had a gutter here.
   classic keeps the stock gutter, like the rail's own bleed rule. */
@media (min-width: 601px) {
  .fi-hz-content .eb-board,
  .fi-hz-content .eb-scope-row,
  .fi-hz-content .eb-filter-row,
  .fi-hz-content .eb-no-matches { margin-left: -19px; margin-right: -19px; }
}
/* and the bottom bleeds into the pane's 20px gutter the same way, leaving
   the rail's 1px sliver against the pane border */
.fi-hz-content .eb-root { margin-bottom: -19px; }
@media (min-width: 601px) and (max-width: 768px) {
  /* the root gutter narrows to 8px here; bleed to 1px, not past the pane */
  .fi-hz-content .eb-board,
  .fi-hz-content .eb-scope-row,
  .fi-hz-content .eb-filter-row,
  .fi-hz-content .eb-no-matches { margin-left: -7px; margin-right: -7px; }
}
.eb-col {
  flex: 1 1 0; min-width: 250px; min-height: 0;
  display: flex; flex-direction: column;
  background: var(--fi-surface-sunken, #f4f6f8);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 8px;
  padding: 8px;
  /* the fallback drag is synthesized mouse events, which would otherwise
     select text across lanes while a card is in flight */
  user-select: none; -webkit-user-select: none;
}
/* the lane's own scroll area. the head + note above it never scroll, so
   the header is pinned by construction. the sticky zero-height pseudos
   paint the scroll shadows: they cast only while their class says content
   is folded past that edge, and they sit above the cards (the pseudos are
   stacking contexts; the cards are not). */
.eb-col-scroll {
  position: relative; flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  overflow-y: auto;
  margin: 0 -4px; padding: 0 4px;
}
.eb-col-scroll::before,
.eb-col-scroll::after {
  content: ''; display: block; flex: 0 0 0px; height: 0;
  position: sticky; z-index: 2; pointer-events: none;
  opacity: 0; transition: opacity .15s;
}
.eb-col-scroll::before { top: 0; order: -1; box-shadow: 0 7px 9px -6px rgba(15, 23, 42, 0.35); }
.eb-col-scroll::after { bottom: 0; box-shadow: 0 -7px 9px -6px rgba(15, 23, 42, 0.35); }
.eb-col-scroll--shadow-top::before { opacity: 1; }
.eb-col-scroll--shadow-bottom::after { opacity: 1; }
/* midnight's lanes are dark, so a navy shadow vanishes — cast deeper black */
[data-fi-theme="midnight"] .eb-col-scroll::before { box-shadow: 0 7px 10px -6px rgba(0, 0, 0, 0.8); }
[data-fi-theme="midnight"] .eb-col-scroll::after { box-shadow: 0 -7px 10px -6px rgba(0, 0, 0, 0.8); }
/* FI-2113 empty lane: say so, centered in the (still droppable) space */
.eb-col-empty {
  position: absolute; left: 6px; right: 6px; top: 6px; bottom: 6px;
  display: flex; align-items: center; justify-content: center;
  text-align: center; padding: 8px;
  font-size: 12px; color: var(--fi-text-muted, #8a94a1);
  border: 1px dashed var(--fi-line-strong, #d6dde6); border-radius: 6px;
  pointer-events: none;
}
/* FI-2113 the resolved window, on the lane it governs — right edge of the
   note row, where it replaces the "last N days" words */
.eb-lane-window { display: inline-flex; gap: 3px; flex-shrink: 0; margin-left: auto; }
.eb-lane-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 1px 7px; font-size: 10.5px; font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.eb-lane-chip:hover { border-color: var(--fi-ops-accent, #3a6e99); }
.eb-lane-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.eb-col-head { display: flex; align-items: center; justify-content: space-between; padding: 2px 2px 0; }
.eb-col-count { font-size: 12px; font-weight: 700; color: var(--fi-text-muted, #6b7280); font-variant-numeric: tabular-nums; }
.eb-col-note {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 11px; color: var(--fi-text-muted, #8a94a1); padding: 2px 2px 6px;
}
/* the lane must stay a drop target when empty */
/* fills the scroll area so the whole visible lane stays a drop target,
   however few cards it holds */
.eb-col-body { flex: 1 0 auto; min-height: 60px; display: flex; flex-direction: column; gap: 6px; }
.eb-col-more {
  flex: 0 0 auto; text-align: center;
  font-size: 11px; color: var(--fi-text-muted, #8a94a1); padding: 8px 2px 4px;
}
/* lane top accents follow the status chip families */
.eb-col--new { box-shadow: inset 0 3px 0 0 #9aa4b1; }
.eb-col--in_progress { box-shadow: inset 0 3px 0 0 #8db8f2; }
.eb-col--awaiting_followup { box-shadow: inset 0 3px 0 0 #f0d97a; }
.eb-col--resolved { box-shadow: inset 0 3px 0 0 #7bcf99; }

/* a real surface, not transparent: the card must read as an object ON the
   sunken lane, not a region of it (fi-2113, matching .cb-card on the
   coaching board). midnight re-resolves both tokens, so the card/lane
   contrast carries to the dark canvas without stamping white cards on it. */
.eb-card {
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line-strong, #dfe4ea);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: grab;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
  transition: border-color .12s, box-shadow .12s;
}
.eb-card:hover { border-color: var(--fi-ops-accent, #3a6e99); }
.eb-card--resolved { opacity: 0.82; }
.eb-card--locked { cursor: pointer; }
/* flagged cards carry the house rail edge (fi-pr-rail / ob-rail): square
   3px accent, soft right side -- not a radius-clipped inset stripe. the
   :hover guards keep the accent from being repainted by .eb-card:hover. */
.eb-card--stale,
.eb-card--high { border-radius: 0 6px 6px 0; }
.eb-card--stale { border-left: 3px solid #e8912d; }
.eb-card--high { border-left: 3px solid #d62f2f; }
.eb-card--stale:hover { border-left-color: #e8912d; }
.eb-card--high:hover { border-left-color: #d62f2f; }

/* ---- the drag, made legible -----------------------------------------
   a drop is over in a blink, so the board narrates it in four places: the
   slot the card left goes hollow, the card in flight lifts off the board,
   the lanes sort themselves into "you may land here" and "not from where
   you started", and the card pulses once where it comes to rest. */

/* the slot left behind: a hole in the lane, not a faded copy of the card.
   the content keeps its space (visibility, not display) so nothing below
   jumps while the card is in the air. */
/* every wash below is a translucent accent rather than a mixed light colour,
   so it reads the same over the light canvas and over midnight's. */
.eb-card--ghost {
  opacity: 1;
  background: rgba(58, 110, 153, 0.12);
  border: 2px dashed var(--fi-ops-accent, #3a6e99);
  box-shadow: none;
}
.eb-card--ghost > * { visibility: hidden; }

/* the card under the cursor. force-fallback makes this our own clone rather
   than the browser's flat drag-image snapshot, so it can actually be styled
   — but sortablejs owns its inline transform/opacity/position, so the lift
   is expressed in border and shadow only. */
/* the one card that DOES need to paint: force-fallback floats this clone
   over the whole page, so a transparent one would show the page through it. */
.eb-card--dragging {
  cursor: grabbing;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.30);
}

/* lanes answer "can this land here?" for as long as a card is in the air */
.eb-col--drop-target {
  background: rgba(58, 110, 153, 0.10);
  outline: 2px solid var(--fi-ops-accent, #3a6e99);
  outline-offset: -1px;
}
.eb-col--drop-target .eb-col-body {
  box-shadow: inset 0 0 0 1px rgba(58, 110, 153, 0.35);
  border-radius: 6px;
}
.eb-col--drop-blocked { opacity: 0.4; }
/* the source lane needs no wash: the hollow slot the card left already
   marks where it came from. */
.eb-col-note--target { color: var(--fi-ops-accent, #3a6e99); font-weight: 700; }
.eb-col-note--blocked { font-style: italic; }

/* the landing. a ring that blooms and fades, green where the move stuck and
   red where the server sent the card back. */
@keyframes eb-pulse-landed {
  0%   { box-shadow: 0 0 0 4px rgba(22, 101, 52, 0.45); border-color: #166534; }
  100% { box-shadow: 0 0 0 4px rgba(22, 101, 52, 0);    border-color: var(--fi-line-strong, #dfe4ea); }
}
@keyframes eb-pulse-rejected {
  0%   { box-shadow: 0 0 0 4px rgba(185, 28, 28, 0.45); border-color: #b91c1c; }
  100% { box-shadow: 0 0 0 4px rgba(185, 28, 28, 0);    border-color: var(--fi-line-strong, #dfe4ea); }
}
/* FI-2173: a card someone else filed or moved, brought in by the quiet
   pass (FI-2215: or one just filed from Add entry). same ring, in the ops
   accent so it is visibly not the green "your drop stuck" one. no midnight
   override targets .eb-card, so the token carries the theme. */
@keyframes eb-pulse-arrived {
  0%   { box-shadow: 0 0 0 4px color-mix(in srgb, var(--fi-ops-accent, #3a6e99) 45%, transparent); border-color: var(--fi-ops-accent, #3a6e99); }
  100% { box-shadow: 0 0 0 4px transparent; border-color: var(--fi-line-strong, #dfe4ea); }
}
.eb-card--landed { animation: eb-pulse-landed 1.5s ease-out 1; }
.eb-card--rejected { animation: eb-pulse-rejected 1.5s ease-out 1; }
.eb-card--arrived { animation: eb-pulse-arrived 1.5s ease-out 1; }

/* the pulses and the lane fades are pure decoration; anyone who has asked
   for less motion gets the colour without the animation. */
@media (prefers-reduced-motion: reduce) {
  .eb-card--landed, .eb-card--rejected, .eb-card--arrived { animation-duration: 1ms; }
  .eb-card--landed { box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.45); }
  .eb-card--rejected { box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.45); }
  .eb-card--arrived { box-shadow: 0 0 0 3px color-mix(in srgb, var(--fi-ops-accent, #3a6e99) 45%, transparent); }
}
.eb-card-top { display: flex; align-items: center; gap: 4px; }
.eb-card-id { font-size: 11px; color: var(--fi-text-muted, #8a94a1); font-variant-numeric: tabular-nums; }
/* no line clamp: the description wraps to as many lines as it needs and the
   card grows with it (the lane already scrolls). overflow-wrap covers the
   pathological case -- an unspaced payload wider than the lane. */
.eb-card-what {
  margin-top: 4px; color: var(--fi-text, #374151); line-height: 1.35;
  overflow-wrap: anywhere;
}
.eb-card-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 8px; margin-top: 6px; }
.eb-meta { font-size: 11px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
/* FI-2197 (#1813) typed charters run to 39 characters, wider than a 250px
   lane's card, so clip to the card; the title keeps the full value. */
.eb-charter { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }
.eb-card-foot { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
.eb-flag { font-size: 11px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.eb-flag--stale { color: #b45309; font-weight: 600; }
.eb-flag--resolved { color: #166534; }
.eb-assignee { font-size: 11px; color: var(--fi-text-muted, #6b7280); }
.eb-assignee--none { font-style: italic; color: var(--fi-text-muted, #9aa4b1); }
.eb-you-chip {
  display: inline-block; padding: 1px 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
}

/* chip families shared with the Event Log (od- copies are scoped there) */
.eb-status-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.eb-status-chip .fa { font-size: 10px; line-height: 1; }
.eb-status-chip--new               { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.eb-status-chip--in_progress       { background: #e7f1ff; color: #0b3d91; border-color: #bcd8fb; }
.eb-status-chip--awaiting_followup { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.eb-status-chip--resolved          { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }

.eb-type-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 7px;
  font-size: 10.5px; line-height: 1.5; font-weight: 600;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.eb-type-chip .fa { font-size: 9.5px; }
.eb-type-chip--incident { background: #fee4d1; color: #b85a1e; border-color: #f0c7a4; }
.eb-type-chip--kudos    { background: #ddf3e3; color: #146230; border-color: #b7e2c4; }
.eb-type-chip--note     { background: #eef1f5; color: #4b5563; border-color: #d6dde6; }

.eb-sev-chip {
  display: inline-flex; align-items: center;
  padding: 1px 7px;
  font-size: 10.5px; line-height: 1.5; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
}
.eb-sev-chip--high { background: #fdeaea; color: #b91c1c; border-color: #f3c2c2; }
.eb-sev-chip--low  { background: #f4f6f8; color: #8a94a1; border-color: #e2e7ec; }
.eb-sev-chip--dim  { opacity: 0.6; }

.eb-cat-chip {
  display: inline-flex; align-items: center;
  padding: 1px 7px;
  font-size: 10.5px; line-height: 1.5;
  border-radius: 4px;
  background: #eef1f5; color: #4b5563; border: 1px solid #e2e7ec;
}

@media (max-width: 768px) {
  /* bottom stays a sliver like desktop: the viewport-bound board leaves
     no page scroll for breathing room to live in */
  .eb-root { padding: 12px 8px 2px 8px; }
  /* dragging is disabled at this width (see isMobileView), so the card must
     not advertise a grab it will not honour — it is a tap target that opens
     the event, where the Move to buttons live. */
  .eb-card { cursor: pointer; }
  /* the search takes the full width of its own row rather than being
     squeezed to nothing beside the scope chips */
  .eb-search { min-width: 100%; margin-left: 0; }

}

@media (max-width: 600px) {
  /* the add button drops to icon-only beside the title (coaching's
     cb-btn-label rule) */
  .eb-btn-label { display: none; }

  /* primary action to the right corner, matching the Event Log/Detail
     rails. authored in this template, so it carries this component's
     scope id even though it renders inside FiPageRail's slot -- no
     ::v-deep needed, unlike the .fi-pr-* overrides below. */
  .eb-act-right { order: 1; }

  /* mobile-only affordances turn on here */
  .eb-mobile-only { display: inline-flex; }
  /* FiPageRail wraps its actions onto a full-width second row on a phone,
     which is right for pages with worded buttons. all three of ours are
     single icons, so they belong beside the title and that row is wasted
     height. un-stretching the actions is not enough on its own — the rail
     is still flex-wrap:wrap — so the wrap is switched off too. the rail
     element is the child's ROOT, so it carries this component's scope id.

     these go through .eb-root deliberately. scoped css stamps an attribute
     on the RAIL's own selectors too, so `.fi-pr-rail[data-v-erm]` and the
     rail's `.fi-pr-rail[data-v-rail]` weigh exactly the same and source
     order decides — and the child's stylesheet is injected last, so it won
     and the header kept wrapping. Going through the root adds a third
     component to the specificity and settles it outright. */
  /* the band matches .od-rail's phone padding (ops_desk.vue) so the board
     and the log read as one family. the button box and glyph sizes come
     from src/assets/css/erm-mobile.css. FiPageRail's nowrap-actions prop
     must not replace these overrides: it sets white-space: nowrap on the
     title, which would clip the favorite star instead of letting the
     title wrap. */
  .eb-root ::v-deep .fi-pr-rail { flex-wrap: nowrap; padding: 10px 12px; gap: 10px; }
  .eb-root ::v-deep .fi-pr-actions { width: auto; flex-shrink: 0; }
  /* with no wrap to fall back on, a long title gives way (clips) rather than
     pushing the icons off the edge. no ellipsis: .fi-pr-title is a flex
     container, so its text is an anonymous flex item and text-overflow
     would not apply to it. */
  .eb-root ::v-deep .fi-pr-title { min-width: 0; overflow: hidden; }
  /* collapsed: every control folds away, search included, so a phone opens
     straight onto the lanes and spends no rows on chrome. the toggle above
     flips it back, and its badge counts what is hidden. */
  .eb-filters-collapsed .eb-scope-row,
  .eb-filters-collapsed .eb-filter-row { display: none; }
  /* each group scrolls on its own line rather than wrapping into a wall */
  .eb-filter-row { flex-direction: column; align-items: flex-start; gap: 8px; }
  .eb-group { flex-wrap: wrap; }
  /* stacked, there is no "far right" to push to — the auto margin would
     shove the Resolved group off to the edge on its own line */
  .eb-group--end { margin-left: 0; }

  /* one lane per screen instead of a ~1000px scrubbable strip. the lane
     takes 86% so the next one's edge stays visible — that sliver is the
     only thing telling you there are three more — and the scroll snaps so
     a swipe lands square on a lane rather than halfway between two. only on
     phones: a tablet has room for the overview and should keep it. */
  .eb-board {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }
  .eb-col {
    flex: 0 0 86%;
    min-width: 0;
    scroll-snap-align: start;
  }
}
</style>
